import React, { useState, KeyboardEvent, useRef, useEffect } from 'react';
import { Send, Paperclip, Mic, MicOff, Square } from 'lucide-react';

// Type definitions for speech recognition
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onCancel?: () => void;
  isLoading: boolean;
  disabled?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, onCancel, isLoading, disabled = false }) => {
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [language, setLanguage] = useState<'en-US' | 'ar-SA'>('en-US');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<any>(null);

  const handleSend = () => {
    if (message.trim() && !isLoading && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleVoiceInput = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    if (isRecording) {
      // Stop recording
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      // Start recording
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        alert('Failed to start voice recording. Please try again.');
      }
    }
  };

  const toggleLanguage = () => {
    const newLanguage = language === 'en-US' ? 'ar-SA' : 'en-US';
    setLanguage(newLanguage);
    
    // Update recognition language if it's already initialized
    if (recognitionRef.current) {
      recognitionRef.current.lang = newLanguage;
    }
  };

  // Simple Arabic romanization to Arabic script converter
  const convertRomanizedToArabic = (text: string): string => {
    const romanizedToArabic: { [key: string]: string } = {
      // Common Arabic words and phrases
      'marhaba': 'مرحبا',
      'ahlan': 'أهلا',
      'shukran': 'شكرا',
      'afwan': 'عفوا',
      'ma\'a salama': 'مع السلامة',
      'sabah al khair': 'صباح الخير',
      'masa al khair': 'مساء الخير',
      'kayf halak': 'كيف حالك',
      'ana bekhair': 'أنا بخير',
      'ismi': 'اسمي',
      'min fadlak': 'من فضلك',
      'la': 'لا',
      'naam': 'نعم',
      'mumkin': 'ممكن',
      'mush mumkin': 'مش ممكن',
      'mafeesh': 'مفيش',
      'yalla': 'يلا',
      'habibi': 'حبيبي',
      'habibti': 'حبيبتي',
      'salam': 'سلام',
      'assalamu alaikum': 'السلام عليكم',
      'wa alaikum assalam': 'وعليكم السلام',
      'bismillah': 'بسم الله',
      'inshallah': 'إن شاء الله',
      'mashallah': 'ما شاء الله',
      'alhamdulillah': 'الحمد لله',
      'astaghfirullah': 'أستغفر الله',
      'subhanallah': 'سبحان الله',
      'allah': 'الله',
      'rasul': 'رسول',
      'nabi': 'نبي',
      'quran': 'قرآن',
      'hadith': 'حديث',
      'sunnah': 'سنة',
      'fiqh': 'فقه',
      'shariah': 'شريعة',
      'halal': 'حلال',
      'haram': 'حرام',
      'makruh': 'مكروه',
      'mustahabb': 'مستحب',
      'mubah': 'مباح',
      'wajib': 'واجب',
      'fard': 'فرض',
      'nafl': 'نفل',
      'tahajjud': 'تهجد',
      'duha': 'ضحى',
      'asr': 'عصر',
      'maghrib': 'مغرب',
      'isha': 'عشاء',
      'fajr': 'فجر',
      'dhuhr': 'ظهر',
      'adhan': 'أذان',
      'iqama': 'إقامة',
      'salah': 'صلاة',
      'sawm': 'صوم',
      'zakat': 'زكاة',
      'hajj': 'حج',
      'umrah': 'عمرة',
      'kaaba': 'كعبة',
      'masjid': 'مسجد',
      'mihrab': 'محراب',
      'minbar': 'منبر',
      'qibla': 'قبلة',
      'wudu': 'وضوء',
      'ghusl': 'غسل',
      'tayammum': 'تيمم',
      'istinja': 'استنجاء',
      'istibra': 'استبراء',
      'istikhara': 'استخارة',
      'dua': 'دعاء',
      'dhikr': 'ذكر',
      'tasbih': 'تسبيح',
      'tahmid': 'تحميد',
      'takbir': 'تكبير',
      'tahlil': 'تهليل',
      'istighfar': 'استغفار',
      'shahada': 'شهادة',
      'kalima': 'كلمة',
      'taqwa': 'تقوى',
      'iman': 'إيمان',
      'islam': 'إسلام',
      'muslim': 'مسلم',
      'mumin': 'مؤمن',
      'kafir': 'كافر',
      'mushrik': 'مشرك',
      'munafiq': 'منافق',
      'fasiq': 'فاسق',
      'zalim': 'ظالم',
      'adil': 'عادل',
      'hakim': 'حاكم',
      'qadi': 'قاضي',
      'mufti': 'مفتي',
      'alim': 'عالم',
      'shaykh': 'شيخ',
      'ustadh': 'أستاذ',
      'talib': 'طالب',
      'faqih': 'فقيه',
      'mujtahid': 'مجتهد',
      'mukallaf': 'مكلف',
      'baligh': 'بالغ',
      'aqil': 'عاقل',
      'rasheed': 'راشد',
      'mukhtar': 'مختار',
      'muharram': 'محرم',
      'safar': 'صفر',
      'rabi al awwal': 'ربيع الأول',
      'rabi al thani': 'ربيع الثاني',
      'jumada al ula': 'جمادى الأولى',
      'jumada al thaniya': 'جمادى الآخرة',
      'rajab': 'رجب',
      'shaaban': 'شعبان',
      'ramadan': 'رمضان',
      'shawwal': 'شوال',
      'dhu al qadah': 'ذو القعدة',
      'dhu al hijjah': 'ذو الحجة'
    };

    let convertedText = text.toLowerCase();
    
    // Replace romanized words with Arabic script
    Object.entries(romanizedToArabic).forEach(([romanized, arabic]) => {
      const regex = new RegExp(`\\b${romanized}\\b`, 'gi');
      convertedText = convertedText.replace(regex, arabic);
    });

    return convertedText;
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [message]);

  // Initialize speech recognition
  useEffect(() => {
    // Check if browser supports speech recognition
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = language;
      
      recognitionRef.current.onstart = () => {
        setIsListening(true);
      };
      
      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        
        // For Arabic, try to convert romanized text to Arabic script
        if (language === 'ar-SA') {
          const convertedText = convertRomanizedToArabic(transcript);
          console.log('Original transcript:', transcript);
          console.log('Converted to Arabic:', convertedText);
          setMessage(convertedText);
        } else {
          setMessage(transcript);
        }
        
        setIsListening(false);
        setIsRecording(false);
      };
      
      recognitionRef.current.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setIsRecording(false);
      };
      
      recognitionRef.current.onend = () => {
        setIsListening(false);
        setIsRecording(false);
      };
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [language]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        handleSend();
      }
      if (e.key === 'Escape') {
        // While a query is running, Esc stops it; otherwise it clears the box.
        if (isLoading) {
          onCancel?.();
        } else {
          setMessage('');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown as any);
    return () => {
      document.removeEventListener('keydown', handleKeyDown as any);
    };
  }, [message, isLoading, disabled, onCancel]);

  return (
    <div className="glass border-t border-white/20 p-4 sm:p-6 relative z-20 shadow-soft">
      {/* Language Toggle */}
      <div className="flex items-center justify-center mb-3">
        <button
          onClick={toggleLanguage}
          className="px-4 py-2 rounded-xl bg-gradient-to-r from-primary-100 to-primary-200 hover:from-primary-200 hover:to-primary-300 text-primary-700 font-medium text-sm transition-all duration-200 shadow-soft hover:shadow-medium"
          title={`Switch to ${language === 'en-US' ? 'Arabic' : 'English'}`}
        >
          {language === 'en-US' ? '🇬🇧 English' : '🇦🇪 العربية'}
        </button>
      </div>
      
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={language === 'en-US' 
              ? "Type your message or use voice input... (Ctrl+Enter to send, Esc to clear)"
              : "اكتب رسالتك أو استخدم الإدخال الصوتي... (Ctrl+Enter للإرسال، Esc للمسح)"
            }
            className="w-full p-4 sm:p-5 border border-white/30 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/50 text-sm sm:text-base min-h-[48px] sm:min-h-[52px] max-h-32 bg-white/80 backdrop-blur-sm transition-all duration-200 focus:bg-white"
            rows={1}
            disabled={disabled}
            dir={language === 'ar-SA' ? 'rtl' : 'ltr'}
          />
        </div>
        
        {/* Voice Input Button */}
        <button
          onClick={handleVoiceInput}
          disabled={isLoading || disabled}
          className={`p-4 sm:p-5 rounded-2xl flex items-center justify-center min-w-[48px] sm:min-w-[52px] h-[48px] sm:h-[52px] transition-all duration-200 transform hover:scale-105 focus-ring ${
            isRecording || isListening
              ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-medium hover:shadow-strong animate-pulse'
              : !isLoading && !disabled
              ? 'bg-gradient-to-r from-secondary-500 to-secondary-600 hover:from-secondary-600 hover:to-secondary-700 text-white shadow-medium hover:shadow-strong'
              : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
          }`}
          title={isRecording 
            ? (language === 'en-US' ? "Stop recording" : "إيقاف التسجيل")
            : (language === 'en-US' ? "Start voice input" : "بدء الإدخال الصوتي")
          }
        >
          {isRecording || isListening ? (
            <MicOff className="w-5 h-5 sm:w-6 sm:h-6" />
          ) : (
            <Mic className="w-5 h-5 sm:w-6 sm:h-6" />
          )}
        </button>
        
        {/* Send / Stop Button — becomes a Stop button while a query is running */}
        <button
          onClick={isLoading ? onCancel : handleSend}
          disabled={isLoading ? false : (!message.trim() || disabled)}
          className={`p-4 sm:p-5 rounded-2xl flex items-center justify-center min-w-[48px] sm:min-w-[52px] h-[48px] sm:h-[52px] transition-all duration-200 transform hover:scale-105 focus-ring ${
            isLoading
              ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white shadow-medium hover:shadow-strong'
              : message.trim() && !disabled
              ? 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-medium hover:shadow-strong'
              : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
          }`}
          title={isLoading
            ? (language === 'en-US' ? 'Stop' : 'إيقاف')
            : (language === 'en-US' ? 'Send' : 'إرسال')
          }
        >
          {isLoading ? (
            <Square className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" />
          ) : (
            <Send className="w-5 h-5 sm:w-6 sm:h-6" />
          )}
        </button>
      </div>
      
              {/* Voice Recording Indicator */}
        {(isRecording || isListening) && (
          <div className="mt-3 flex items-center justify-center gap-2 text-sm text-red-600 animate-pulse">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-ping"></div>
            <span>
              {isListening 
                ? (language === 'en-US' ? "Listening..." : "أستمع...")
                : (language === 'en-US' ? "Recording..." : "تسجيل...")
              }
            </span>
          </div>
        )}
        

    </div>
  );
};

export default ChatInput;
